import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  Users, 
  FileText, 
  BarChart3, 
  Settings, 
  AlertCircle, 
  Database, 
  CreditCard, 
  Search, 
  Filter, 
  ArrowUpRight, 
  ArrowDownRight,
  ShieldCheck,
  Activity,
  ChevronRight,
  UserCheck,
  Zap,
  Clock,
  Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import BroadcastEmails from '@/components/admin/BroadcastEmails';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'usage' | 'payments' | 'health' | 'broadcast'>('overview');
  const [userInsights, setUserInsights] = useState<any[]>([]);
  const [businessStats, setBusinessStats] = useState<any>(null);
  const [systemErrors, setSystemErrors] = useState<any[]>([]);
  const [allProfiles, setAllProfiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const { data: users } = await supabase
        .from('view_ai_cost_per_user')
        .select('*')
        .order('total_cost_usd', { ascending: false });
        
      const { data: bStats } = await supabase
        .from('view_platform_business_stats')
        .select('*')
        .single();
      
      const { data: profiles } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      const { data: payErrors } = await supabase
        .from('dead_letter_transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (users) setUserInsights(users);
      if (bStats) setBusinessStats(bStats);
      if (profiles) setAllProfiles(profiles);
      if (payErrors) setSystemErrors(payErrors);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'users', name: 'User Management', icon: Users },
    { id: 'usage', name: 'AI & Usage', icon: Zap },
    { id: 'payments', name: 'Payments', icon: CreditCard },
    { id: 'health', name: 'System Health', icon: Activity },
    { id: 'broadcast', name: 'Broadcast Emails', icon: Mail },
  ];

  const renderOverview = () => (
    <div className="space-y-8">
      {/* High-level Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Total Users', value: allProfiles.length, icon: Users, trend: '+12%', color: 'text-blue-500' },
          { label: 'Total Forms', value: businessStats?.total_forms || 0, icon: FileText, trend: '+5%', color: 'text-purple-500' },
          { label: 'Submissions', value: businessStats?.total_submissions || 0, icon: BarChart3, trend: '+18%', color: 'text-emerald-500' },
          { label: 'Revenue (XLM)', value: (businessStats?.total_xlm_revenue || 0).toFixed(2), icon: CreditCard, trend: '+2%', color: 'text-amber-500' },
        ].map((stat, i) => (
          <Card key={i} className="border-border/40 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={cn("p-2 rounded-lg bg-muted/50", stat.color)}>
                  <stat.icon className="h-5 w-5" />
                </div>
                <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 border-none font-bold">
                  {stat.trend}
                </Badge>
              </div>
              <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
              <h3 className="text-2xl font-bold tracking-tight">{stat.value}</h3>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Users Table Snippet */}
        <Card className="lg:col-span-2 border-border/40 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-bold">Recent Signups</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs text-primary font-bold" onClick={() => setActiveTab('users')}>
              View All
            </Button>
          </CardHeader>
          <CardContent className="p-0">
             <div className="overflow-x-auto">
                <table className="w-full text-left">
                   <thead>
                      <tr className="border-b border-border/40 bg-muted/20">
                         <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">User</th>
                         <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Plan</th>
                         <th className="p-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Date</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-border/40">
                      {allProfiles.slice(0, 5).map((p, i) => (
                        <tr key={i} className="hover:bg-muted/30 transition-colors group">
                           <td className="p-4">
                              <div className="flex items-center gap-3">
                                 <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs uppercase">
                                    {p.email?.[0] || 'U'}
                                 </div>
                                 <span className="text-sm font-medium truncate max-w-[150px]">{p.email || 'Anonymous'}</span>
                              </div>
                           </td>
                           <td className="p-4">
                              <Badge variant="outline" className="capitalize text-[10px] font-bold">
                                 {p.plan || 'Free'}
                              </Badge>
                           </td>
                           <td className="p-4 text-xs text-muted-foreground">
                              {new Date(p.created_at).toLocaleDateString()}
                           </td>
                        </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          </CardContent>
        </Card>

        {/* System Alerts Snippet */}
        <Card className="border-border/40 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Recent Alerts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
             {systemErrors.length > 0 ? systemErrors.map((err, i) => (
               <div key={i} className="flex gap-4 p-3 rounded-lg bg-destructive/5 border border-destructive/10 animate-in fade-in">
                  <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                  <div className="min-w-0">
                     <p className="text-xs font-bold text-destructive uppercase tracking-widest mb-1">Payment Failure</p>
                     <p className="text-sm text-muted-foreground truncate">{err.reason}</p>
                  </div>
               </div>
             )) : (
               <div className="text-center py-12 text-muted-foreground text-sm">
                  <ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  No critical alerts
               </div>
             )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderUsers = () => (
    <Card className="border-border/40 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between border-b border-border/40 pb-6">
        <div>
           <CardTitle className="text-xl font-bold">User Management</CardTitle>
           <p className="text-sm text-muted-foreground">Manage and monitor all platform users.</p>
        </div>
        <div className="flex items-center gap-3">
           <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search users..." className="pl-9 h-10 w-64 rounded-xl" />
           </div>
           <Button variant="outline" className="rounded-xl h-10 gap-2">
              <Filter className="h-4 w-4" />
              <span>Filter</span>
           </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
         <div className="overflow-x-auto">
            <table className="w-full text-left">
               <thead>
                  <tr className="bg-muted/20 border-b border-border/40">
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Profile</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Business Name</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Plan Status</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Created</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-right">Action</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-border/40">
                  {allProfiles.map((p, i) => (
                    <tr key={i} className="hover:bg-muted/10 transition-colors group">
                       <td className="p-6">
                          <div className="flex items-center gap-4">
                             <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                                {p.display_name?.[0] || p.email?.[0] || 'U'}
                             </div>
                             <div>
                                <p className="text-sm font-bold tracking-tight">{p.display_name || 'No Name Set'}</p>
                                <p className="text-xs text-muted-foreground font-mono">{p.email}</p>
                             </div>
                          </div>
                       </td>
                       <td className="p-6">
                          <p className="text-sm text-foreground">{p.business_name || '—'}</p>
                          <p className="text-[10px] text-muted-foreground italic truncate max-w-[120px]">{p.website || 'No website'}</p>
                       </td>
                       <td className="p-6">
                          <Badge 
                            variant="secondary" 
                            className={cn(
                              "font-bold uppercase tracking-widest text-[9px] px-2.5 py-0.5",
                              p.plan === 'pro' ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground border-transparent"
                            )}
                          >
                             {p.plan || 'Free'}
                          </Badge>
                       </td>
                       <td className="p-6 text-sm text-muted-foreground">
                          {new Date(p.created_at).toLocaleDateString()}
                       </td>
                       <td className="p-6 text-right">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                             <ChevronRight className="h-4 w-4" />
                          </Button>
                       </td>
                    </tr>
                  ))}
               </tbody>
            </table>
         </div>
      </CardContent>
    </Card>
  );

  const renderUsage = () => (
    <Card className="border-border/40 shadow-sm">
      <CardHeader>
         <CardTitle className="text-xl font-bold flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            AI Usage & Resource Consumption
         </CardTitle>
         <p className="text-sm text-muted-foreground">Monitoring LLM costs and latency per user segment.</p>
      </CardHeader>
      <CardContent className="p-0">
         <div className="overflow-x-auto">
            <table className="w-full text-left">
               <thead>
                  <tr className="bg-muted/20 border-b border-border/40">
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Consumer</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground text-center">Requests</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total Cost (USD)</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Avg Latency</th>
                     <th className="p-6 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Status</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-border/40">
                  {userInsights.map((u, i) => (
                    <tr key={i} className="hover:bg-muted/10 transition-colors">
                       <td className="p-6">
                          <div className="flex items-center gap-3">
                             <UserCheck className="h-4 w-4 text-muted-foreground" />
                             <span className="text-sm font-medium">{u.email || u.user_id?.slice(0, 12)}</span>
                          </div>
                       </td>
                       <td className="p-6 text-center text-sm font-bold">{u.total_requests}</td>
                       <td className="p-6">
                          <span className="text-sm font-mono text-foreground font-bold tracking-tight">
                             ${u.total_cost_usd?.toFixed(4)}
                          </span>
                       </td>
                       <td className="p-6">
                          <div className="flex items-center gap-2">
                             <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden w-16">
                                <div className="h-full bg-primary" style={{ width: `${Math.min(100, (u.avg_latency_ms / 5000) * 100)}%` }} />
                             </div>
                             <span className="text-xs text-muted-foreground font-mono">{Math.round(u.avg_latency_ms)}ms</span>
                          </div>
                       </td>
                       <td className="p-6">
                          <Badge variant="outline" className="bg-emerald-500/5 text-emerald-600 border-emerald-500/20 text-[9px] font-bold">OPTIMAL</Badge>
                       </td>
                    </tr>
                  ))}
               </tbody>
            </table>
         </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-surface-lowest text-foreground font-sans">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-surface-lowest/80 backdrop-blur-md border-b border-border/10 px-8 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
           <div className="flex items-center gap-4">
              <div className="p-2.5 bg-primary/10 rounded-xl">
                 <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
              <div>
                 <h1 className="text-xl font-display font-bold tracking-tightest">Bravax Forms <span className="text-muted-foreground font-normal ml-2">· Administration</span></h1>
                 <div className="flex items-center gap-2 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Global Systems Operational</span>
                 </div>
              </div>
           </div>
           <div className="flex items-center gap-4">
              <Badge variant="outline" className="bg-surface-low text-muted-foreground border-border/10 font-bold uppercase tracking-widest text-[9px] px-4 py-1.5 rounded-lg shadow-sm">
                 v1.4.2 · Production
              </Badge>
           </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-8 py-10 flex flex-col lg:flex-row gap-12">
        {/* Sidebar Nav */}
        <aside className="w-full lg:w-72 shrink-0 space-y-2">
           <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-4 mb-4">Core Controls</p>
           {tabs.map((tab) => (
             <button
               key={tab.id}
               onClick={() => setActiveTab(tab.id as any)}
               className={cn(
                 "w-full flex items-center gap-3 px-5 py-4 rounded-2xl text-sm font-bold transition-all",
                 activeTab === tab.id 
                  ? "bg-primary text-primary-foreground shadow-xl shadow-primary/20 scale-[1.02]" 
                  : "text-muted-foreground hover:text-foreground hover:bg-surface-low/50"
               )}
             >
                <tab.icon className="h-4 w-4" />
                {tab.name}
             </button>
           ))}
           
           <div className="pt-12 space-y-6">
              <div className="p-6 rounded-[2rem] bg-surface-low/40 border border-border/5 backdrop-blur-sm">
                 <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-5">System Resources</p>
                 <ul className="space-y-4">
                    <li>
                       <a href="#" className="flex items-center justify-between text-xs font-bold hover:text-primary transition-colors group">
                          Documentation
                          <ArrowUpRight className="h-3 w-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                       </a>
                    </li>
                    <li>
                       <a href="#" className="flex items-center justify-between text-xs font-bold hover:text-primary transition-colors group">
                          Postgres Console
                          <Database className="h-3 w-3" />
                       </a>
                    </li>
                    <li>
                       <a href="#" className="flex items-center justify-between text-xs font-bold hover:text-primary transition-colors group">
                          Global Activity
                          <Activity className="h-3 w-3" />
                       </a>
                    </li>
                 </ul>
              </div>
           </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 animate-in fade-in duration-500">
           {isLoading ? (
             <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-4">
                   <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                   <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">Aggregating Data...</p>
                </div>
             </div>
           ) : (
             <>
                {activeTab === 'broadcast' && <BroadcastEmails />}
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'users' && renderUsers()}
                {activeTab === 'usage' && renderUsage()}
                {activeTab === 'payments' && (
                  <div className="flex flex-col items-center justify-center py-32 bg-card rounded-3xl border border-border/40 shadow-sm text-center px-6">
                     <CreditCard className="h-12 w-12 text-muted-foreground mb-4 opacity-20" />
                     <h3 className="text-xl font-bold mb-2">Payment Infrastructure</h3>
                     <p className="text-muted-foreground max-w-md">Detailed transaction monitoring and billing lifecycle management is being optimized.</p>
                     <Button className="mt-8 rounded-xl h-10 gap-2" onClick={() => setActiveTab('overview')}>
                        Return to Overview
                     </Button>
                  </div>
                )}
                {activeTab === 'health' && (
                  <div className="space-y-6">
                     <Card className="border-border/40 shadow-sm overflow-hidden">
                        <CardHeader className="bg-muted/10 border-b border-border/40">
                           <CardTitle className="text-lg font-bold flex items-center gap-2">
                              <Activity className="h-5 w-5 text-emerald-500" />
                              System Operational Status
                           </CardTitle>
                        </CardHeader>
                        <CardContent className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                           {[
                             { label: 'Edge Functions', status: 'Healthy', uptime: '99.99%', icon: Zap },
                             { label: 'Postgres Database', status: 'Healthy', uptime: '100%', icon: Database },
                             { label: 'Redis Cache', status: 'Active', uptime: '99.95%', icon: Activity },
                             { label: 'Real-time Gateway', status: 'Active', uptime: '99.98%', icon: ShieldCheck },
                           ].map((sys, i) => (
                             <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-border/20 bg-muted/5">
                                <div className="flex items-center gap-4">
                                   <div className="p-2 rounded-lg bg-background border border-border/40 shadow-sm">
                                      <sys.icon className="h-4 w-4 text-primary" />
                                   </div>
                                   <div>
                                      <p className="text-sm font-bold">{sys.label}</p>
                                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">{sys.uptime} Uptime</p>
                                   </div>
                                </div>
                                <Badge className="bg-emerald-500/10 text-emerald-600 border-none font-bold uppercase tracking-widest text-[9px] px-3">
                                   {sys.status}
                                </Badge>
                             </div>
                           ))}
                        </CardContent>
                     </Card>
                     
                     <Card className="border-destructive/20 shadow-sm overflow-hidden">
                        <CardHeader className="bg-destructive/5 border-b border-destructive/10">
                           <CardTitle className="text-lg font-bold flex items-center gap-2 text-destructive">
                              <AlertCircle className="h-5 w-5" />
                              Recent Failures & Exceptions
                           </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                           <div className="divide-y divide-border/40">
                              {systemErrors.map((err, i) => (
                                <div key={i} className="p-6 flex items-center justify-between group hover:bg-muted/10 transition-colors">
                                   <div className="flex items-center gap-4">
                                      <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                                         <Clock className="h-5 w-5 text-destructive" />
                                      </div>
                                      <div>
                                         <p className="text-sm font-bold text-foreground">{err.reason}</p>
                                         <p className="text-[10px] text-muted-foreground font-mono">{new Date(err.created_at).toLocaleString()}</p>
                                      </div>
                                   </div>
                                   <Badge variant="outline" className="text-[9px] font-mono text-muted-foreground">ID: {err.id?.slice(0, 8)}</Badge>
                                </div>
                              ))}
                           </div>
                        </CardContent>
                     </Card>
                  </div>
                )}
             </>
           )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
